import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  getPurchases, createPurchase, updatePurchase, deletePurchase,
  decidePurchase, markPurchaseBought, getPurchaseEvents,
  getApprovalMap, setApprover
} from '../services/api';
import Navbar from '../components/common/Navbar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatMoney, formatNumber, formatDateTime } from '../utils/formatters';
import './PurchasesPage.css';

/* Иконки вместо эмодзи (см. Navbar.jsx). */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IconPlus = () => <Icon><path d="M12 4V20M4 12H20" /></Icon>;
const IconPen = () => <Icon><path d="M4 22H20" /><path d="M13.8881 3.66293L14.6296 2.92142C15.8581 1.69286 17.85 1.69286 19.0786 2.92142C20.3071 4.14999 20.3071 6.14188 19.0786 7.37044L18.3371 8.11195M13.8881 3.66293C13.8881 3.66293 13.9807 5.23862 15.3711 6.62894C16.7614 8.01926 18.3371 8.11195 18.3371 8.11195M13.8881 3.66293L7.07106 10.4799C6.60933 10.9416 6.37846 11.1725 6.17992 11.4271C5.94571 11.7273 5.74491 12.0522 5.58107 12.396C5.44219 12.6874 5.33894 12.9972 5.13245 13.6167L4.25745 16.2417M4.25745 16.2417L4.04356 16.8833C3.94194 17.1882 4.02128 17.5243 4.2485 17.7515C4.47573 17.9787 4.81182 18.0581 5.11667 17.9564L5.75834 17.7426L8.38334 16.8675C9.00282 16.6611 9.31256 16.5578 9.60398 16.4189C9.94775 16.2551 10.2727 16.0543 10.5729 15.8201C10.8275 15.6215 11.0584 15.3907 11.5201 14.9289L18.3371 8.11195M5.75834 17.7426L4.25745 16.2417" /></Icon>;
const IconRestart = () => <Icon><path d="M18.364 8.04928L17.6569 7.34217C14.5327 4.21798 9.46734 4.21798 6.34315 7.34217C3.21895 10.4664 3.21895 15.5317 6.34315 18.6559C9.46734 21.7801 14.5327 21.7801 17.6569 18.6559C19.4737 16.8391 20.234 14.3658 19.9377 11.9995M18.364 3.80664V8.04928H14.1213" /></Icon>;
const IconCheckCircle = () => <Icon><circle cx="12" cy="12" r="10" /><path d="M8.5 12.5L10.5 14.5L15.5 9.5" /></Icon>;
const IconClose = () => <Icon><path d="M19.0068 5L5.00684 19" /><path d="M19 19L5 5" /></Icon>;
const IconUndoLeft = () => <Icon><path d="M4 7H15C16.8692 7 17.8039 7 18.5 7.40193C18.9561 7.66523 19.3348 8.04394 19.5981 8.49999C20 9.19615 20 10.1308 20 12C20 13.8692 20 14.8038 19.5981 15.5C19.3348 15.9561 18.9561 16.3348 18.5 16.5981C17.8039 17 16.8692 17 15 17H8.00001M7 10L4 7L7 4" /></Icon>;
const IconCart = () => <Icon><path d="M2 3L2.26491 3.0883C3.58495 3.52832 4.24497 3.74832 4.62248 4.2721C5 4.79587 5 5.49159 5 6.88304V9.5C5 12.3284 5 13.7426 5.87868 14.6213C6.75736 15.5 8.17157 15.5 11 15.5H19" /><path d="M7.5 18C8.32843 18 9 18.6716 9 19.5C9 20.3284 8.32843 21 7.5 21C6.67157 21 6 20.3284 6 19.5C6 18.6716 6.67157 18 7.5 18Z" /><path d="M16.5 18.0001C17.3284 18.0001 18 18.6716 18 19.5001C18 20.3285 17.3284 21.0001 16.5 21.0001C15.6716 21.0001 15 20.3285 15 19.5001C15 18.6716 15.6716 18.0001 16.5 18.0001Z" /><path d="M11 9H8" /><path d="M5 6H16.4504C18.5054 6 19.5328 6 19.9775 6.67426C20.4221 7.34853 20.0173 8.29294 19.2078 10.1818L18.7792 11.1818C18.4013 12.0636 18.2123 12.5045 17.8366 12.7523C17.4609 13 16.9812 13 16.0218 13H5" /></Icon>;
const IconBillList = () => <Icon><path d="M16.755 2H7.24502C6.08614 2 5.50671 2 5.03939 2.16261C4.15322 2.47096 3.45748 3.18719 3.15795 4.09946C3 4.58055 3 5.17705 3 6.37006V20.3742C3 21.2324 3.985 21.6878 4.6081 21.1176C4.97417 20.7826 5.52583 20.7826 5.8919 21.1176L6.375 21.5597C7.01659 22.1468 7.98341 22.1468 8.625 21.5597C9.26659 20.9726 10.2334 20.9726 10.875 21.5597C11.5166 22.1468 12.4834 22.1468 13.125 21.5597C13.7666 20.9726 14.7334 20.9726 15.375 21.5597C16.0166 22.1468 16.9834 22.1468 17.625 21.5597L18.1081 21.1176C18.4742 20.7826 19.0258 20.7826 19.3919 21.1176C20.015 21.6878 21 21.2324 21 20.3742V6.37006C21 5.17705 21 4.58055 20.842 4.09946C20.5425 3.18719 19.8468 2.47096 18.9606 2.16261C18.4933 2 17.9139 2 16.755 2Z" /><path d="M10.5 11L17 11" /><path d="M7 11H7.5" /><path d="M7 7.5H7.5" /><path d="M7 14.5H7.5" /><path d="M10.5 7.5H17" /><path d="M10.5 14.5H17" /></Icon>;
const IconWarning = () => <Icon><path d="M5.31171 10.7615C8.23007 5.58716 9.68925 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.31034 17.856 2.52291 15.7061 4.94805 11.4063L5.31171 10.7615Z" /><path d="M12 8V13" /><path d="M12 16H12.0001" /></Icon>;
const IconExternalLink = () => <Icon><path d="M21 3L12 12M12 6.65625V12H17.3438" /><path d="M22 12C22 16.714 22 19.0711 20.5355 20.5355C19.0711 22 16.714 22 12 22C7.28595 22 4.92893 22 3.46447 20.5355C2 19.0711 2 16.714 2 12C2 7.28595 2 4.92893 3.46447 3.46447C4.92893 2 7.28595 2 12 2" /></Icon>;

const STATUS_META = {
  pending:   { title: 'На согласовании', cls: 'st-pending' },
  revision:  { title: 'На доработке',    cls: 'st-revision' },
  approved:  { title: 'Согласовано',     cls: 'st-approved' },
  purchased: { title: 'Куплено',         cls: 'st-purchased' },
  rejected:  { title: 'Не согласовано',  cls: 'st-rejected' },
};

/* Значок события в ленте истории */
const EVENT_ICON = {
  created: <IconPlus />,
  updated: <IconPen />,
  resubmitted: <IconRestart />,
  approved: <IconCheckCircle />,
  rejected: <IconClose />,
  revision: <IconUndoLeft />,
  purchased: <IconCart />,
};

/* Подпись под решением зависит от того, чем оно было */
const DECISION_LABEL = {
  revision: 'Вернул на доработку',
  approved: 'Согласовал',
  rejected: 'Отклонил',
};

/* Три исхода решения согласующего: тексты модалки и вид кнопки */
const VERDICT_META = {
  approved: {
    heading: 'Согласовать закупку',
    action: 'Согласовать',
    btn: 'ok',
    commentHint: ' (необязательно)',
  },
  revision: {
    heading: 'Вернуть закупку на доработку',
    action: 'Отправить на доработку',
    btn: 'rev',
    commentHint: ' (что нужно исправить)',
  },
  rejected: {
    heading: 'Отклонить закупку',
    action: 'Не согласовать',
    btn: 'no',
    commentHint: ' (причина отказа)',
  },
};

const SCOPES = [
  { key: 'all',      title: 'Все закупки' },
  { key: 'mine',     title: 'Мои закупки' },
  { key: 'incoming', title: 'На моё согласование' },
];

const EMPTY_FORM = { title: '', item_url: '', price: '', quantity: '1', comment: '' };

/* «1 закупка», «2 закупки», «5 закупок» — иначе в шапке выходит «1 закупок» */
const plural = (n, one, few, many) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
};

/* Стоимость считается и на сервере тоже — здесь это только предпросмотр,
   чтобы сотрудник видел сумму до отправки. */
const previewTotal = (price, quantity) => {
  const p = parseFloat(String(price).replace(',', '.'));
  const q = parseFloat(String(quantity).replace(',', '.'));
  if (!isFinite(p) || !isFinite(q)) return null;
  return p * q;
};

const PurchasesPage = () => {
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('list');          // list | map
  const [data, setData] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [scope, setScope] = useState('all');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);     // закупка, которую правим
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);       // карточка закупки по клику на строку
  const [events, setEvents] = useState([]);         // история открытой карточки
  const [bought, setBought] = useState(null);       // закупка, которую отмечаем купленной
  const [boughtForm, setBoughtForm] = useState({ actual_price: '', actual_quantity: '', comment: '' });
  const [boughtError, setBoughtError] = useState('');
  const [boughtSaving, setBoughtSaving] = useState(false);
  const [decision, setDecision] = useState(null);   // { purchase, verdict }
  const [decisionComment, setDecisionComment] = useState('');
  const [decisionError, setDecisionError] = useState('');
  const [decisionSaving, setDecisionSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);       // id закупки, которую удаляем
  const [approverSavingId, setApproverSavingId] = useState(null); // id сотрудника, чью строку карты сохраняем

  const load = useCallback(async () => {
    try {
      setError('');
      const res = await getPurchases({ scope, status, search });
      setData(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось загрузить закупки');
    } finally {
      setLoading(false);
    }
  }, [scope, status, search]);

  const loadMap = useCallback(async () => {
    try {
      setMapData(await getApprovalMap());
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось загрузить карту согласований');
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && tab === 'list') load();
  }, [isAuthenticated, tab, load]);

  useEffect(() => {
    if (isAuthenticated && tab === 'map') loadMap();
  }, [isAuthenticated, tab, loadMap]);

  /* Esc закрывает верхнюю открытую модалку — детали закупки, отметку о
     покупке, форму или решение согласующего (в этом порядке приоритета). */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (decision) setDecision(null);
      else if (bought) setBought(null);
      else if (formOpen) setFormOpen(false);
      else if (detail) setDetail(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [decision, bought, formOpen, detail]);

  const openCreate = () => {
    setDetail(null);
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (p) => {
    setDetail(null);
    setEditing(p);
    setForm({
      title: p.title,
      item_url: p.item_url || '',
      price: String(p.price),
      quantity: String(p.quantity),
      comment: p.comment || '',
    });
    setFormError('');
    setFormOpen(true);
  };

  /* Переоформление отклонённой: заводим новую закупку с теми же полями,
     а отказ остаётся в истории — его не переписываем. */
  const openReissue = (p) => {
    setDetail(null);
    setEditing(null);
    setForm({
      title: p.title,
      item_url: p.item_url || '',
      price: String(p.price),
      quantity: String(p.quantity),
      comment: p.comment || '',
    });
    setFormError('');
    setFormOpen(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setFormError('');

    const payload = {
      title: form.title.trim(),
      item_url: form.item_url.trim(),
      comment: form.comment.trim(),
      price: parseFloat(String(form.price).replace(',', '.')),
      quantity: parseFloat(String(form.quantity).replace(',', '.')),
    };

    if (!payload.title) return setFormError('Укажите, что закупаем');
    if (!(payload.price > 0)) return setFormError('Цена должна быть больше нуля');
    if (!(payload.quantity > 0)) return setFormError('Количество должно быть больше нуля');

    setSaving(true);
    try {
      if (editing) await updatePurchase(editing.id, payload);
      else await createPurchase(payload);
      setFormOpen(false);
      await load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Не удалось сохранить закупку');
    } finally {
      setSaving(false);
    }
  };

  const submitDecision = async () => {
    setDecisionError('');
    setDecisionSaving(true);
    try {
      await decidePurchase(decision.purchase.id, decision.verdict, decisionComment.trim());
      setDecision(null);
      setDecisionComment('');
      await load();
    } catch (err) {
      setDecisionError(err.response?.data?.error || 'Не удалось сохранить решение');
    } finally {
      setDecisionSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Удалить закупку «${p.title}»?`)) return;
    setRemovingId(p.id);
    try {
      await deletePurchase(p.id);
      setDetail(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось удалить закупку');
    } finally {
      setRemovingId(null);
    }
  };

  /* История подгружается только при открытии карточки: тянуть её
     для каждой строки списка незачем. */
  const openDetail = async (p) => {
    setDetail(p);
    setEvents([]);
    try {
      const data = await getPurchaseEvents(p.id);
      setEvents(data.events);
    } catch {
      setEvents([]);
    }
  };

  const openBought = (p) => {
    setDetail(null);
    setBoughtError('');
    // Подставляем план: чаще всего купили ровно по нему, и править нечего
    setBoughtForm({
      actual_price: String(p.price),
      actual_quantity: String(p.quantity),
      comment: '',
    });
    setBought(p);
  };

  const submitBought = async () => {
    setBoughtError('');
    const payload = {
      actual_price: parseFloat(String(boughtForm.actual_price).replace(',', '.')),
      actual_quantity: parseFloat(String(boughtForm.actual_quantity).replace(',', '.')),
      comment: boughtForm.comment.trim(),
    };
    if (!(payload.actual_price > 0)) return setBoughtError('Фактическая цена должна быть больше нуля');
    if (!(payload.actual_quantity > 0)) return setBoughtError('Количество должно быть больше нуля');

    setBoughtSaving(true);
    try {
      await markPurchaseBought(bought.id, payload);
      setBought(null);
      await load();
    } catch (err) {
      setBoughtError(err.response?.data?.error || 'Не удалось отметить покупку');
    } finally {
      setBoughtSaving(false);
    }
  };

  const changeApprover = async (userId, approverId) => {
    setApproverSavingId(userId);
    try {
      const res = await setApprover(userId, approverId ? Number(approverId) : null);
      setMapData((prev) => ({ ...prev, map: res.map }));
    } catch (err) {
      setError(err.response?.data?.error || 'Не удалось сохранить карту согласований');
    } finally {
      setApproverSavingId(null);
    }
  };

  if (authLoading || !isAuthenticated) return <LoadingSpinner />;

  const stats = data?.stats;
  const meId = data?.current_user_id;
  const isAdmin = user?.role === 'admin';
  const canDecide = (p) => p.status === 'pending' && (p.approver_id === meId || isAdmin);
  const isMine = (p) => p.created_by === meId || isAdmin;
  const canEdit = (p) => ['pending', 'revision'].includes(p.status) && isMine(p);
  /* Переоформляет только сам автор: новая заявка заводится от его имени
     и уходит его согласующему, поэтому админу тут подменять некого. */
  const canReissue = (p) => p.status === 'rejected' && p.created_by === meId;
  /* Покупку отмечает тот, кто её делал: автор, а также админ и управляющий */
  const canMarkBought = (p) => p.status === 'approved'
    && (p.created_by === meId || ['admin', 'manager'].includes(user?.role));
  const total = previewTotal(form.price, form.quantity);
  const boughtTotal = previewTotal(boughtForm.actual_price, boughtForm.actual_quantity);

  const askDecision = (purchase, verdict) => {
    setDetail(null);
    setDecisionComment('');
    setDecisionError('');
    setDecision({ purchase, verdict });
  };

  /* Один набор действий и в строке таблицы, и в карточке.
     stopPropagation — чтобы кнопка не открывала карточку заодно. */
  const renderActions = (p) => (
    <div className="pu-actions" onClick={(e) => e.stopPropagation()}>
      {canDecide(p) && (
        <>
          <button className="pu-act ok" onClick={() => askDecision(p, 'approved')}>
            Согласовать
          </button>
          <button className="pu-act rev" onClick={() => askDecision(p, 'revision')}>
            Отправить на доработку
          </button>
          <button className="pu-act no" onClick={() => askDecision(p, 'rejected')}>
            Отклонить
          </button>
        </>
      )}
      {canEdit(p) && (
        <>
          <button className="pu-act" onClick={() => openEdit(p)}>
            {p.status === 'revision' ? 'Доработать и отправить' : 'Изменить'}
          </button>
          <button className="pu-act del" onClick={() => remove(p)} disabled={removingId === p.id}>
            {removingId === p.id ? 'Удаляем…' : 'Удалить'}
          </button>
        </>
      )}
      {canMarkBought(p) && (
        <button className="pu-act ok" onClick={() => openBought(p)}>Отметить купленным</button>
      )}
      {canReissue(p) && (
        <button className="pu-act" onClick={() => openReissue(p)}>Переоформить</button>
      )}
    </div>
  );

  return (
    <div className="dashboard-container">
      <Navbar user={user} isAdmin={isAdmin} onLogout={logout} />

      <div className="dashboard-header">
        <div className="date-badge">
          <IconBillList /> {stats
            ? `${formatNumber(stats.total)} ${plural(stats.total, 'закупка', 'закупки', 'закупок')} · `
              + `${formatNumber(stats.pending)} ${plural(stats.pending, 'ждёт', 'ждут', 'ждут')} решения`
            : '—'}
        </div>
        <button className="btn-primary" type="button" onClick={openCreate}>
          <IconPlus /> Новая закупка
        </button>
      </div>

      {/* Закупки и карта согласований — два независимых экрана */}
      <div className="pu-tabs">
        <button
          className={`pu-tab ${tab === 'list' ? 'active' : ''}`}
          onClick={() => setTab('list')}
        >
          Закупки
        </button>
        <button
          className={`pu-tab ${tab === 'map' ? 'active' : ''}`}
          onClick={() => setTab('map')}
        >
          Карта согласований
        </button>
      </div>

      {error && (
        <div className="pu-error">
          <span><IconWarning /></span><strong>{error}</strong>
        </div>
      )}

      {tab === 'list' && loading && <LoadingSpinner />}

      {tab === 'list' && !loading && data && (
        <>
          {/* Карточки статусов, они же фильтр */}
          <div className="pu-cards">
            <button
              className={`pu-card ${!status ? 'selected' : ''}`}
              onClick={() => setStatus('')}
            >
              <span className="pu-card-title">Всего</span>
              <span className="pu-card-num">{formatNumber(stats.total)}</span>
              <span className="pu-card-sum">закупок в разделе</span>
            </button>
            <button
              className={`pu-card st-pending ${status === 'pending' ? 'selected' : ''}`}
              onClick={() => setStatus(status === 'pending' ? '' : 'pending')}
            >
              <span className="pu-card-title">На согласовании</span>
              <span className="pu-card-num">{formatNumber(stats.pending)}</span>
              <span className="pu-card-sum">{formatMoney(stats.sum_pending)}</span>
            </button>
            <button
              className={`pu-card st-revision ${status === 'revision' ? 'selected' : ''}`}
              onClick={() => setStatus(status === 'revision' ? '' : 'revision')}
            >
              <span className="pu-card-title">На доработке</span>
              <span className="pu-card-num">{formatNumber(stats.revision)}</span>
              <span className="pu-card-sum">{formatMoney(stats.sum_revision)}</span>
            </button>
            <button
              className={`pu-card st-approved ${status === 'approved' ? 'selected' : ''}`}
              onClick={() => setStatus(status === 'approved' ? '' : 'approved')}
            >
              <span className="pu-card-title">Согласовано</span>
              <span className="pu-card-num">{formatNumber(stats.approved)}</span>
              <span className="pu-card-sum">{formatMoney(stats.sum_approved)}</span>
              <span className="pu-card-hint">ждут покупки</span>
            </button>
            <button
              className={`pu-card st-purchased ${status === 'purchased' ? 'selected' : ''}`}
              onClick={() => setStatus(status === 'purchased' ? '' : 'purchased')}
            >
              <span className="pu-card-title">Куплено</span>
              <span className="pu-card-num">{formatNumber(stats.purchased)}</span>
              <span className="pu-card-sum">{formatMoney(stats.sum_purchased)}</span>
              {/* Итог по факту против плана: сразу видно, укладываемся или нет */}
              <span className={`pu-card-hint ${stats.diff_purchased > 0 ? 'over' : 'under'}`}>
                {stats.diff_purchased
                  ? `${stats.diff_purchased > 0 ? '+' : '−'}${formatMoney(Math.abs(stats.diff_purchased))} к плану`
                  : 'ровно по плану'}
              </span>
            </button>
            <button
              className={`pu-card st-rejected ${status === 'rejected' ? 'selected' : ''}`}
              onClick={() => setStatus(status === 'rejected' ? '' : 'rejected')}
            >
              <span className="pu-card-title">Не согласовано</span>
              <span className="pu-card-num">{formatNumber(stats.rejected)}</span>
              <span className="pu-card-sum">отклонены ответственным</span>
            </button>
          </div>

          <div className="pu-filters panel">
            <div className="pu-chips">
              {SCOPES.map((s) => (
                <button
                  key={s.key}
                  className={`pu-chip ${scope === s.key ? 'active' : ''}`}
                  onClick={() => setScope(s.key)}
                >
                  {s.title}
                  {s.key === 'incoming' && stats.incoming > 0 && (
                    <span className="pu-chip-n">{stats.incoming}</span>
                  )}
                  {/* Вернули на доработку — этого ждут от самого сотрудника */}
                  {s.key === 'mine' && stats.my_revision > 0 && (
                    <span className="pu-chip-n">{stats.my_revision}</span>
                  )}
                </button>
              ))}
            </div>
            <input
              type="search"
              className="pu-search"
              aria-label="Поиск по товару или сотруднику"
              placeholder="Поиск по товару или сотруднику…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="panel pu-table-panel">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Товар</th>
                    <th>Цена</th>
                    <th>Кол-во</th>
                    <th>Стоимость</th>
                    <th>Сотрудник</th>
                    <th>Согласует</th>
                    <th>Статус</th>
                    <th>Комментарий</th>
                    <th>Создана</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.purchases.map((p) => (
                    <tr
                      key={p.id}
                      className="pu-clickable"
                      onClick={() => openDetail(p)}
                      title="Открыть карточку закупки"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openDetail(p);
                        }
                      }}
                    >
                      <td>
                        <div className="pu-title">{p.title}</div>
                        {p.item_url && (
                          <a
                            className="pu-link"
                            href={p.item_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconExternalLink /> ссылка на товар
                          </a>
                        )}
                      </td>
                      <td>{formatMoney(p.price)}</td>
                      <td>{formatNumber(p.quantity)}</td>
                      <td className="pu-money">
                        {formatMoney(p.total)}
                        {p.actual_total != null && (
                          <div className={`pu-fact ${p.diff > 0 ? 'over' : 'under'}`}>
                            факт {formatMoney(p.actual_total)}
                            {p.diff ? ` (${p.diff > 0 ? '+' : '−'}${formatMoney(Math.abs(p.diff))})` : ''}
                          </div>
                        )}
                      </td>
                      <td>{p.author_name || '—'}</td>
                      <td>
                        {p.approver_name || <span className="pu-muted">не назначен</span>}
                      </td>
                      <td>
                        <span className={`pu-badge ${STATUS_META[p.status].cls}`}>
                          {STATUS_META[p.status].title}
                        </span>
                      </td>
                      {/* Комментарий согласующего живёт отдельной графой:
                          в статусе остаётся только сам статус */}
                      <td>
                        {p.status !== 'pending' && p.decided_by_name ? (
                          <div className="pu-decision">
                            {p.decision_comment
                              ? <div className="pu-decision-text">«{p.decision_comment}»</div>
                              : <div className="pu-muted">без комментария</div>}
                            <div className="pu-decision-who">
                              {DECISION_LABEL[p.status]}: {p.decided_by_name}, {formatDateTime(p.decided_at)}
                            </div>
                          </div>
                        ) : (
                          <span className="pu-muted">—</span>
                        )}
                      </td>
                      <td className="pu-date">{formatDateTime(p.created_at)}</td>
                      <td>{renderActions(p)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data.purchases.length === 0 && (
              <div className="pu-empty">
                Закупок по этому фильтру нет.
              </div>
            )}
          </div>

          <p className="pu-note">
            Стоимость считает сервер по формуле «цена × количество» — вручную её
            не изменить. Решение принимает ответственный из карты согласований:
            согласовать, вернуть на доработку или отклонить. Закупку с доработки
            автор правит и отправляет заново — она снова уходит на согласование.
            Отклонённую изменить нельзя, но её можно переоформить: заведётся новая заявка с теми же
            полями, а отказ останется в истории. Согласованную закупку после покупки
            отмечают купленной с фактической ценой — так видно расхождение с планом
            и остаётся список того, что согласовали, но ещё не купили. Клик по строке
            открывает карточку со всей историей закупки.
          </p>
        </>
      )}

      {tab === 'map' && !mapData && <LoadingSpinner />}

      {tab === 'map' && mapData && (
        <>
          <div className="panel pu-table-panel">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Сотрудник</th>
                    <th>Должность</th>
                    <th>Кто согласует его закупки</th>
                    <th>Изменено</th>
                  </tr>
                </thead>
                <tbody>
                  {mapData.map.map((row) => (
                    <tr key={row.user_id}>
                      <td><div className="pu-title">{row.full_name}</div></td>
                      <td>{row.role_name}</td>
                      <td>
                        {mapData.can_edit ? (
                          <select
                            className="pu-select"
                            value={row.approver_id || ''}
                            onChange={(e) => changeApprover(row.user_id, e.target.value)}
                            disabled={approverSavingId === row.user_id}
                          >
                            <option value="">— не назначен —</option>
                            {mapData.map
                              .filter((u) => u.user_id !== row.user_id)
                              .map((u) => (
                                <option key={u.user_id} value={u.user_id}>
                                  {u.full_name} · {u.role_name}
                                </option>
                              ))}
                          </select>
                        ) : (
                          row.approver_name || <span className="pu-muted">не назначен</span>
                        )}
                      </td>
                      <td className="pu-date">
                        {row.updated_at ? formatDateTime(row.updated_at) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="pu-note">
            {mapData.can_edit
              ? 'Ответственный подставляется в закупку в момент её создания. Если поменять карту позже, уже отправленные закупки останутся у прежнего согласующего.'
              : 'Карту согласований настраивают администратор и управляющий. Здесь показано, кто согласует закупки каждого сотрудника.'}
          </p>
        </>
      )}

      {/* Карточка закупки — открывается кликом по строке */}
      {detail && (
        <div className="pu-modal-overlay" onClick={() => setDetail(null)}>
          <div className="pu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pu-modal-head">
              <h3>Карточка закупки</h3>
              <button className="pu-close" onClick={() => setDetail(null)} aria-label="Закрыть">×</button>
            </div>

            <div className="pu-detail-head">
              <div className="pu-detail-title">{detail.title}</div>
              <span className={`pu-badge ${STATUS_META[detail.status].cls}`}>
                {STATUS_META[detail.status].title}
              </span>
            </div>

            <div className="pu-detail-total">
              <span>{formatMoney(detail.price)} × {formatNumber(detail.quantity)}</span>
              <strong>{formatMoney(detail.total)}</strong>
            </div>

            <dl className="pu-detail-rows">
              <div>
                <dt>Сотрудник</dt>
                <dd>{detail.author_name || '—'}</dd>
              </div>
              <div>
                <dt>Согласует</dt>
                <dd>{detail.approver_name || <span className="pu-muted">не назначен</span>}</dd>
              </div>
              <div>
                <dt>Ссылка на товар</dt>
                <dd>
                  {detail.item_url
                    ? (
                      <a
                        className="pu-link"
                        href={detail.item_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {detail.item_url}
                      </a>
                    )
                    : <span className="pu-muted">—</span>}
                </dd>
              </div>
              <div>
                <dt>Комментарий сотрудника</dt>
                <dd>{detail.comment || <span className="pu-muted">—</span>}</dd>
              </div>
              <div>
                <dt>Создана</dt>
                <dd>{formatDateTime(detail.created_at)}</dd>
              </div>
              <div>
                <dt>Изменена</dt>
                <dd>{formatDateTime(detail.updated_at)}</dd>
              </div>
              {detail.revision_count > 0 && (
                <div>
                  <dt>Возвращали на доработку</dt>
                  <dd>
                    {detail.revision_count}{' '}
                    {plural(detail.revision_count, 'раз', 'раза', 'раз')}
                  </dd>
                </div>
              )}
              {detail.actual_total != null && (
                <>
                  <div>
                    <dt>Куплено по факту</dt>
                    <dd>
                      {formatMoney(detail.actual_price)} × {formatNumber(detail.actual_quantity)} ={' '}
                      <strong>{formatMoney(detail.actual_total)}</strong>
                    </dd>
                  </div>
                  <div>
                    <dt>Отклонение от плана</dt>
                    <dd className={detail.diff > 0 ? 'pu-over' : 'pu-under'}>
                      {detail.diff
                        ? `${detail.diff > 0 ? 'дороже на ' : 'дешевле на '}${formatMoney(Math.abs(detail.diff))}`
                        : 'ровно по плану'}
                    </dd>
                  </div>
                  <div>
                    <dt>Отметил покупку</dt>
                    <dd>{detail.purchased_by_name}, {formatDateTime(detail.purchased_at)}</dd>
                  </div>
                </>
              )}
            </dl>

            {detail.decided_by_name && (
              <div className={`pu-detail-decision ${STATUS_META[detail.status].cls}`}>
                <strong>{DECISION_LABEL[detail.status]}: {detail.decided_by_name}</strong>
                <div className="pu-decision-who">{formatDateTime(detail.decided_at)}</div>
                {detail.decision_comment && <div>«{detail.decision_comment}»</div>}
              </div>
            )}

            {/* Лента событий: она помнит и те решения, которые затёрла доработка */}
            <div className="pu-history">
              <div className="pu-history-head">История</div>
              {events.length === 0 && <div className="pu-muted">Событий пока нет</div>}
              {events.map((e) => (
                <div key={e.id} className="pu-event">
                  <span className={`pu-event-icon ev-${e.event}`}>{EVENT_ICON[e.event] || '•'}</span>
                  <div className="pu-event-body">
                    <div className="pu-event-title">
                      {e.event_name}
                      <span className="pu-event-who"> · {e.user_name || 'система'}</span>
                    </div>
                    {e.details && <div className="pu-event-details">{e.details}</div>}
                    {e.comment && <div className="pu-event-comment">«{e.comment}»</div>}
                    <div className="pu-event-time">{formatDateTime(e.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>

            {renderActions(detail)}
          </div>
        </div>
      )}

      {/* Отметка о покупке */}
      {bought && (
        <div className="pu-modal-overlay" onClick={() => setBought(null)}>
          <div className="pu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pu-modal-head">
              <h3>Отметить купленным</h3>
              <button className="pu-close" onClick={() => setBought(null)} aria-label="Закрыть">×</button>
            </div>

            <div className="pu-decision-item">
              <div className="pu-title">{bought.title}</div>
              <div className="pu-decision-sum">
                План: {formatMoney(bought.price)} × {formatNumber(bought.quantity)} ={' '}
                <strong>{formatMoney(bought.total)}</strong>
              </div>
            </div>

            <div className="pu-row">
              <label className="pu-field">
                <span>Фактическая цена, ₽</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={boughtForm.actual_price}
                  onChange={(e) => setBoughtForm({ ...boughtForm, actual_price: e.target.value })}
                  autoFocus
                />
              </label>
              <label className="pu-field">
                <span>Фактическое количество</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={boughtForm.actual_quantity}
                  onChange={(e) => setBoughtForm({ ...boughtForm, actual_quantity: e.target.value })}
                />
              </label>
            </div>

            <div className="pu-total">
              <span>Фактическая стоимость</span>
              <strong>{boughtTotal > 0 ? formatMoney(boughtTotal) : '—'}</strong>
            </div>

            {boughtTotal > 0 && boughtTotal !== bought.total && (
              <div className={`pu-diff ${boughtTotal > bought.total ? 'over' : 'under'}`}>
                {boughtTotal > bought.total ? 'Дороже плана на ' : 'Дешевле плана на '}
                <strong>{formatMoney(Math.abs(boughtTotal - bought.total))}</strong>
              </div>
            )}

            <label className="pu-field">
              <span>Комментарий (необязательно)</span>
              <textarea
                rows="2"
                value={boughtForm.comment}
                onChange={(e) => setBoughtForm({ ...boughtForm, comment: e.target.value })}
                placeholder="Где купили, почему цена изменилась"
              />
            </label>

            {boughtError && <div className="pu-form-error">{boughtError}</div>}

            <div className="pu-modal-foot">
              <button type="button" className="pu-btn" onClick={() => setBought(null)} disabled={boughtSaving}>
                Отмена
              </button>
              <button type="button" className="pu-btn ok" onClick={submitBought} disabled={boughtSaving}>
                {boughtSaving ? 'Сохраняем…' : 'Отметить купленным'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Форма закупки */}
      {formOpen && (
        <div className="pu-modal-overlay" onClick={() => setFormOpen(false)}>
          <div className="pu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pu-modal-head">
              <h3>
                {editing
                  ? (editing.status === 'revision' ? 'Доработать закупку' : 'Изменить закупку')
                  : 'Новая закупка'}
              </h3>
              <button className="pu-close" onClick={() => setFormOpen(false)} aria-label="Закрыть">×</button>
            </div>

            {/* Замечание согласующего держим перед глазами, пока автор правит */}
            {editing?.status === 'revision' && editing.decision_comment && (
              <div className="pu-revision-note">
                <strong>Вернул на доработку: {editing.decided_by_name}</strong>
                <div>«{editing.decision_comment}»</div>
              </div>
            )}

            <form onSubmit={submitForm}>
              <label className="pu-field">
                <span>Что закупаем</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Например: вёдра для парной"
                  autoFocus
                />
              </label>

              <label className="pu-field">
                <span>Ссылка на товар</span>
                <input
                  type="url"
                  value={form.item_url}
                  onChange={(e) => setForm({ ...form, item_url: e.target.value })}
                  placeholder="https://…"
                />
              </label>

              <div className="pu-row">
                <label className="pu-field">
                  <span>Цена за единицу, ₽</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="0"
                  />
                </label>
                <label className="pu-field">
                  <span>Количество</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    placeholder="1"
                  />
                </label>
              </div>

              <div className="pu-total">
                <span>Стоимость</span>
                <strong>{total != null && total > 0 ? formatMoney(total) : '—'}</strong>
              </div>

              <label className="pu-field">
                <span>Комментарий</span>
                <textarea
                  rows="3"
                  value={form.comment}
                  onChange={(e) => setForm({ ...form, comment: e.target.value })}
                  placeholder="Зачем нужно, к какому сроку"
                />
              </label>

              {formError && <div className="pu-form-error">{formError}</div>}

              <div className="pu-modal-foot">
                <button type="button" className="pu-btn" onClick={() => setFormOpen(false)}>
                  Отмена
                </button>
                <button type="submit" className="pu-btn primary" disabled={saving}>
                  {saving
                    ? 'Сохраняем…'
                    : (editing
                      ? (editing.status === 'revision' ? 'Отправить на согласование' : 'Сохранить')
                      : 'Отправить на согласование')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Решение по закупке */}
      {decision && (
        <div className="pu-modal-overlay" onClick={() => setDecision(null)}>
          <div className="pu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pu-modal-head">
              <h3>{VERDICT_META[decision.verdict].heading}</h3>
              <button className="pu-close" onClick={() => setDecision(null)} aria-label="Закрыть">×</button>
            </div>

            <div className="pu-decision-item">
              <div className="pu-title">{decision.purchase.title}</div>
              <div className="pu-decision-sum">
                {formatMoney(decision.purchase.price)} × {formatNumber(decision.purchase.quantity)} =
                {' '}<strong>{formatMoney(decision.purchase.total)}</strong>
              </div>
              <div className="pu-comment">Заявил: {decision.purchase.author_name}</div>
            </div>

            <label className="pu-field">
              <span>Комментарий{VERDICT_META[decision.verdict].commentHint}</span>
              <textarea
                rows="3"
                value={decisionComment}
                onChange={(e) => setDecisionComment(e.target.value)}
                autoFocus
              />
            </label>

            {decisionError && <div className="pu-form-error">{decisionError}</div>}

            <div className="pu-modal-foot">
              <button type="button" className="pu-btn" onClick={() => setDecision(null)} disabled={decisionSaving}>
                Отмена
              </button>
              <button
                type="button"
                className={`pu-btn ${VERDICT_META[decision.verdict].btn}`}
                onClick={submitDecision}
                disabled={decisionSaving}
              >
                {decisionSaving ? 'Сохраняем…' : VERDICT_META[decision.verdict].action}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchasesPage;
